<template>
  <div class="payment">
    <card is-large has-outline>
      <form class="form">
        <div class="form__group">
          <text-input
            label="Email"
            type="email"
            :autofocus="true"
            v-model="form.email" :required="true" />
        </div>
        <div class="form__group">
          <select-input
            id="season"
            name="season"
            label="Season"
            placeholder="Select season"
            :options="seasonOptions"
            v-model="form.season"
            :disabled="disabled || !!id"
            required
          />
        </div>
        <div class="form__group">
          <select-input
            id="currency"
            name="currency"
            label="Paid in"
            placeholder="Select currency"
            :options="currencies"
            v-model="form.currency"
            :disabled="disabled || !!id"
            required
          />
        </div>
        <div class="form__group">
          <text-input
            v-if="id"
            :disabled="true"
            label="Discount" v-model="discount" />
          <discount
            v-else-if="!id && form.amount"
            v-bind="discountParam"
            v-model="payment" />
        </div>
        <div class="form__group" v-if="form.currency && form.season">
          <text-input
          :disabled="true"
          label="Paid" v-model="amountPaid" />
        </div>
        <div class="form__submit">
          <button
            :disabled="!formIsValid"
            @click.prevent="submit">Save</button>
          <button
            v-if="id && form.processor === 'cash'"
            @click.prevent="remove" class="is-light" :disabled="disabled">Delete</button>
        </div>
      </form>
    </card>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import store from '@/store';
import TextInput from '@/components/TextInput.vue';
import SelectInput from '@/components/SelectInput.vue';
import Card from '@/components/Card.vue';
import Discount from '@/views/comic/pay/Discount.vue';

export default {
  name: 'PaymentForm',
  components: {
    TextInput,
    SelectInput,
    Card,
    Discount,
  },
  beforeRouteEnter: (to, from, next) => {
    store.dispatch('fetchAllDiscounts').then(() => next());
  },
  props: {
    id: String,
    amount: Number,
    currency: String,
    description: String,
    discount: String,
    email: String,
    processor: String,
    season: String,
    disabled: Boolean,
  },
  data() {
    return {
      form: {
        season: {},
        discount: {},
      },
      currencies: [
        { key: 1, value: 'NGN', label: 'NGN' },
        { key: 2, value: 'GBP', label: 'GBP' },
      ],
      response: null,
      payment: null,
    };
  },
  computed: {
    ...mapGetters(['getAllSeasons', 'getDiscount']),
    discountParam() {
      return {
        amount: this.form.amount,
        currency: this.form.currency,
      };
    },
    invalidDiscount() {
      const { discount, currency } = this.form;
      const exists = discount && !this.getDiscount({ code: discount, currency });
      const discountMismatch = this.$props.discount
        && (this.$props.discount !== this.form.discount);

      return discountMismatch || exists;
    },
    seasonOptions() {
      return (
        this.getAllSeasons
        && this.getAllSeasons.map(({ id, name }) => ({ key: id, value: name, label: name }))
      );
    },
    seasonInfo() {
      return this.form.season && this.getAllSeasons.find(({ name }) => name
        .toLowerCase() === this.form.season.toLowerCase());
    },
    price() {
      return this.form.currency
        && this.seasonInfo.prices
          .find(({ currency }) => currency.toLowerCase() === this.form.currency.toLowerCase());
    },
    amountPaid() {
      const { amount, currency } = this.form;
      const value = (this.$props.amount / 100) || (amount / 100);
      return `${currency} ${value.toLocaleString()}`;
    },
    formIsValid() {
      const {
        currency,
        email,
        season,
      } = this.form;
      return email && currency && season && !this.invalidDiscount;
    },
  },
  watch: {
    $route() {
      this.setForm();
    },
    price(val) {
      if (val) {
        this.form.amount = val.amount;
      }
    },
    payment(value) {
      if (value && value.amount) {
        this.applyDiscount(value);
        return;
      }
      this.form.amount = this.price.amount;
    },
  },
  methods: {
    applyDiscount(discount) {
      this.form.amount = (this.form.amount - discount.amount);
      this.form.discount = discount.code;
    },
    setForm() {
      const { $props } = this;
      this.form = {
        id: $props.id,
        amount: $props.amount,
        currency: $props.currency,
        discount: $props.discount,
        description: $props.description || null,
        email: $props.email,
        processor: $props.processor || 'cash',
        season: $props.season,
      };
    },
    setAmount() {
      const { prices } = this.seasonInfo;
      const price = prices && prices.find(({ currency }) => currency === this.form.currency);
      this.form.amount = price.amount;
    },
    submit() {
      const formValue = { ...this.form };
      const discount = this.getDiscount({
        code: this.form.discount,
        currency: this.form.currency,
      });
      this.$emit('submit', {
        ...formValue,
        season: this.seasonInfo,
        discount: discount || null,
      });
    },
    remove() {
      const formValue = { ...this.form };
      const discount = this.getDiscount({
        code: this.form.discount,
        currency: this.form.currency,
      });
      this.$emit('remove', {
        ...formValue,
        season: this.seasonInfo,
        discount: discount || null,
      });
    },
  },
  created() {
    this.setForm();
  },
};
</script>

<style lang="scss" scoped>
.payment {
  max-width: 520px;
  margin: 30px auto;
  .form {
    text-align: left;
    &__group + .form__group {
      margin-top: 20px;
    }
    &__submit {
      margin-top: 30px;
      display: flex;
      justify-content: space-between;
    }
    .error {
      margin-top: 5px;
      font-size: var(--font-input);
      color: var(--color-link-inactive);
      font-weight: 400;
    }
  }
}
</style>
